import { useQuery } from '@tanstack/react-query';

import { getTaskResults } from '@/lib/api/generated/api';
import type { TaskResultsView } from '@/lib/api/generated/model';
import { LIVE_POLL_MS } from '../liveCadence';
import { taskKeys } from '../queryKeys';

export interface UseTaskResultsOptions {
  /** Whether to read at all. Only an open drawer does (spec 095 FR-022). */
  enabled: boolean;
  /**
   * Whether to keep re-reading on the live cadence. True only while the
   * Results tab is the one shown *and* a run of the task is in progress
   * (FR-023); the drawer decides, so there is one polling observer.
   */
  poll: boolean;
  /**
   * Whether the task still has a run in progress. A finished task's results
   * are written once and never change, so they are never stale (FR-024) — the
   * same rule `useExecutionTranscript` applies to a finished run's transcript.
   */
  live: boolean;
}

/**
 * What a task produced: per run, the recorded summary and verified links, plus
 * the file changes attributed to the task (spec 095).
 *
 * Read once when the drawer opens, on whichever tab. That one read is what
 * decides whether the Results tab is offered and which tab the drawer lands on
 * (research R1): the workspace aggregate carries no signal that a result
 * exists, so the drawer has to ask. Every other consumer of this query — the
 * panel itself — subscribes to the same key with `poll: false`, so the drawer's
 * options are the only ones that turn the interval on.
 */
export function useTaskResults(
  workspaceId: string,
  taskId: string,
  { enabled, poll, live }: UseTaskResultsOptions,
) {
  const query = useQuery({
    queryKey: taskKeys.results(workspaceId, taskId),
    queryFn: async (): Promise<TaskResultsView> => {
      const response = await getTaskResults(workspaceId, taskId);
      if (response.status !== 200) {
        throw new Error('The results could not be loaded.');
      }
      return response.data;
    },
    enabled,
    refetchInterval: poll ? LIVE_POLL_MS : false,
    staleTime: live ? 0 : Infinity,
    retry: false,
  });

  return {
    results: query.data,
    // `isPending` is true before an unenabled query has ever run, so a caller
    // pairs it with `enabled`, as the transcript hook's callers do.
    isPending: query.isPending,
    loadFailed: query.isError,
    retry: () => void query.refetch(),
  };
}
